import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/** Mirrors server/src/mac-tools.ts MAC_PERMISSIONS. */
export type MacPermission = 'read' | 'ask' | 'auto' | 'bypass';
export const DEFAULT_MAC_PERMISSION: MacPermission = 'ask';

export const MAC_PERMISSION_OPTIONS: { id: MacPermission; label: string; sub: string }[] = [
  {
    id: 'read',
    label: 'Только чтение',
    sub: 'Смотреть файлы и систему. Запись, удаление и команды запрещены',
  },
  {
    id: 'ask',
    label: 'Спрашивать',
    sub: 'Каждая запись, удаление и команда — только после вашего «Разрешить»',
  },
  {
    id: 'auto',
    label: 'Авто',
    sub: 'Запись и обычные команды сразу; удаление, sudo, git push, ssh и подобное — с подтверждением',
  },
  {
    id: 'bypass',
    label: 'Без вопросов',
    sub: 'Полный доступ без подтверждений. Только если вы уверены в задаче',
  },
];

export function isMacPermission(v: unknown): v is MacPermission {
  return MAC_PERMISSION_OPTIONS.some((o) => o.id === v);
}

export type MacApproval = {
  id: string;
  sessionId: string;
  tool: string;
  summary: string;
  detail: string | null;
  createdAt: number;
};

const approvalsKey = (sessionId: string) => ['mac-approvals', sessionId] as const;

/** Pending Mac tool calls. Polled only while a run in this chat is streaming. */
export function useMacApprovals(sessionId: string, active: boolean) {
  return useQuery({
    queryKey: approvalsKey(sessionId),
    enabled: active,
    refetchInterval: active ? 1500 : false,
    queryFn: async ({ signal }) => {
      const r = await api<{ approvals: MacApproval[] }>(`/sessions/${sessionId}/mac-approvals`, {
        signal,
      });
      return r.approvals;
    },
  });
}

export function useResolveMacApproval(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, approve }: { id: string; approve: boolean }) => {
      await api(`/sessions/${sessionId}/mac-approvals/${id}`, {
        method: 'POST',
        body: { approve },
      });
    },
    onMutate: ({ id }) => {
      qc.setQueryData<MacApproval[]>(approvalsKey(sessionId), (prev) =>
        prev?.filter((a) => a.id !== id),
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: approvalsKey(sessionId) });
    },
  });
}
